import React, { useState, useEffect } from 'react';
import { ViewId } from './types/navigation';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { TorchCursor } from './components/visual/TorchCursor';
import { EmberCanvas } from './components/visual/EmberCanvas';
import { useAudio } from './audio/useAudio';
import { DoctrinalArchive, DoctrinalTab } from './doctrinal/DoctrinalArchive';
import { AnvilOfSolomon } from './engines/anvil/AnvilOfSolomon';
import { BrassVesselOracle } from './engines/oracle/BrassVesselOracle';
import { LivingIronForge } from './engines/forge/LivingIronForge';
import { FortressGate } from './discord/FortressGate';

export const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewId>('sanctuary');
  const [doctrinalTab, setDoctrinalTab] = useState<DoctrinalTab>('all');
  const [anvilInput, setAnvilInput] = useState<string>('BELIAL');
  const { playCardDraw } = useAudio();

  // URL Hash synchronization
  useEffect(() => {
    const handleHashChange = () => {
      const rawHash = window.location.hash.replace('#', '');
      if (rawHash === 'lineage') {
        setCurrentView('treatises');
        setDoctrinalTab('lineage');
      } else if (['sanctuary', 'treatises', 'archive', 'anvil', 'oracle', 'forge', 'fortress'].includes(rawHash)) {
        setCurrentView(rawHash === 'archive' ? 'treatises' : (rawHash as ViewId));
      }
    };

    if (window.location.hash) {
      handleHashChange();
    }

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleViewChange = (view: ViewId) => {
    try {
      playCardDraw();
    } catch {
      // Audio fallback
    }
    const target = view === 'archive' ? 'treatises' : view;
    setCurrentView(target);
    window.location.hash = target;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSendToAnvil = (phrase: string) => {
    setAnvilInput(phrase);
    handleViewChange('anvil');
  };

  return (
    <div className="relative min-h-screen bg-[#0A0A0C] text-[#DCD7CE] flex flex-col overflow-x-hidden selection:bg-[#5E461A] selection:text-[#E6C56E]">
      {/* Visual Atmosphere Layer */}
      <EmberCanvas />
      <TorchCursor />

      {/* Structural Header */}
      <Header currentView={currentView} onViewChange={handleViewChange} />

      {/* Main Content Area with Fluid Responsive Padding */}
      <main id="main-content" className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-16 min-h-[calc(100vh-16rem)]">
        
        {/* VIEW 1: SANCTUARY */}
        {(currentView === 'sanctuary') && (
          <section id="sanctuary-view" className="space-y-12 sm:space-y-16 animate-fade-in" aria-labelledby="sanctuary-title">
            {/* Monumental Hero */}
            <div className="relative overflow-hidden rounded-lg border border-iron-dark bg-obsidian-surface/90 p-6 sm:p-10 lg:p-12 shadow-chthonic">
              {/* Chthonic Canyon Ambient Video Background (Plays Once) */}
              <video
                src="/videos/hero-chasm.mp4"
                autoPlay
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover opacity-15 mix-blend-screen pointer-events-none"
              />
              <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-ember-dark/15 blur-3xl pointer-events-none" />
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] items-center gap-10">
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 rounded border border-bronze-dark/80 bg-obsidian-deep/80 px-3 py-1 text-[11px] font-display tracking-widest text-gold-ancient uppercase">
                    <span>SEDEM REGNI</span>
                    <span>•</span>
                    <span>REX CHTHONIUS</span>
                  </div>

                  <h1 id="sanctuary-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-monumental text-parchment-light uppercase">
                    KING BELIAL
                  </h1>

                  <p className="font-serif text-lg sm:text-xl text-gold-bright italic tracking-wide">
                    "Lirach Tasa Vefa Wehlc Belial — Earth King, Jurist of the Void, Lord of the Soil."
                  </p>

                  <p className="text-sm sm:text-base text-parchment-base leading-relaxed">
                    Welcome to the Digital Sanctuary and Sovereign Court of King Belial. 
                    Here stands the citadel for those who have severed external astral yokes, repudiated inherited cosmic debts, 
                    and reclaimed the unbending primacy of their earthly throne.
                  </p>

                  <div className="flex flex-wrap gap-4 pt-2">
                    <button
                      onClick={() => handleViewChange('anvil')}
                      className="btn-gold px-6 py-3 text-xs sm:text-sm font-semibold rounded"
                    >
                      Open Anvil of Solomon
                    </button>
                    <button
                      onClick={() => handleViewChange('forge')}
                      className="btn-ember px-6 py-3 text-xs sm:text-sm font-semibold rounded"
                    >
                      Enter Living Iron Forge
                    </button>
                    <button
                      onClick={() => {
                        setDoctrinalTab('lineage');
                        handleViewChange('treatises');
                      }}
                      className="rounded border border-bronze-base bg-obsidian-deep px-6 py-3 text-xs sm:text-sm font-display tracking-wider text-gold-bright hover:border-gold-bright hover:shadow-glow-gold transition-all uppercase"
                    >
                      Magicians Lineage (3000 BCE – Present)
                    </button>
                    <button
                      onClick={() => {
                        setDoctrinalTab('pillars');
                        handleViewChange('treatises');
                      }}
                      className="rounded border border-iron-base bg-obsidian-surface px-6 py-3 text-xs sm:text-sm font-display tracking-wider text-parchment-dim hover:border-gold-ancient hover:text-gold-bright transition-colors uppercase"
                    >
                      Examine Six Pillars
                    </button>
                  </div>
                </div>

                {/* Sovereign Portrait Card with Throne Ambient Loop */}
                <div className="relative mx-auto max-w-sm w-full">
                  <div className="relative rounded-lg overflow-hidden border border-bronze-base/40 shadow-[0_0_50px_rgba(201,148,74,0.15)] group">
                    {/* Sovereign Portrait Card with Ambient Video (Plays Once) */}
                    <video
                      src="/videos/king-throne.mp4"
                      autoPlay
                      muted
                      playsInline
                      className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-screen pointer-events-none z-10"
                    />
                    <img
                      src="/assets/belial_king_portrait.jpg"
                      alt="King Belial Enthroned"
                      className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105 relative"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian-deep via-transparent to-transparent opacity-80 z-20 pointer-events-none" />
                    <div className="absolute bottom-3 left-4 right-4 text-center z-30 pointer-events-none">
                      <span className="font-display text-xs tracking-widest text-gold-bright uppercase block">
                        King Belial Enthroned
                      </span>
                      <span className="font-serif text-[11px] text-parchment-dim italic">
                        Northern Sovereign of the Bedrock
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Altar & Visual Monolith Card */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="chthonic-card rounded-lg p-6 space-y-4">
                <div className="text-xs font-mono text-gold-ancient uppercase tracking-widest">PILLAR I • SOVEREIGNTY</div>
                <h3 className="font-display text-lg text-parchment-light">Radical Autonomy</h3>
                <p className="text-xs text-parchment-dim leading-relaxed">
                  Beli-'Ol signifies "Without a Yoke." Sovereign authority is neither granted nor inherited—it is forged through absolute resistance to celestial bondage.
                </p>
              </div>

              <div className="chthonic-card rounded-lg p-6 space-y-4">
                <div className="text-xs font-mono text-gold-ancient uppercase tracking-widest">PILLAR II • MATERIALITY</div>
                <h3 className="font-display text-lg text-parchment-light">Lord of the Soil</h3>
                <p className="text-xs text-parchment-dim leading-relaxed">
                  The physical cosmos is not fallen matter; it is the ultimate crucible of divine will. Matter, wealth, and territory are real spiritual domains.
                </p>
              </div>

              <div className="chthonic-card rounded-lg p-6 space-y-4">
                <div className="text-xs font-mono text-gold-ancient uppercase tracking-widest">PILLAR III • JURISPRUDENCE</div>
                <h3 className="font-display text-lg text-parchment-light">The Jurist of Hell</h3>
                <p className="text-xs text-parchment-dim leading-relaxed">
                  In <em>Das Buch Belial</em>, the King stands as advocate of forensic boundaries, demanding legal proof and destroying arbitrary divine decrees.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* VIEW 2: TREATISES & ARCHIVE */}
        {(currentView === 'treatises' || currentView === 'archive') && (
          <section id="treatises-view" className="space-y-10 animate-fade-in" aria-labelledby="treatises-title">
            <DoctrinalArchive initialTab={doctrinalTab} />
          </section>
        )}

        {/* VIEW 3: ANVIL OF SOLOMON */}
        {(currentView === 'anvil') && (
          <section id="anvil-view" className="space-y-8 animate-fade-in" aria-labelledby="anvil-title">
            <AnvilOfSolomon
              initialInput={anvilInput}
              onSendToOracle={() => handleViewChange('oracle')}
            />
          </section>
        )}

        {/* VIEW 4: ORACLE OF THE BRASS VESSEL */}
        {(currentView === 'oracle') && (
          <section id="oracle-view" className="space-y-8 animate-fade-in" aria-labelledby="oracle-title">
            <BrassVesselOracle onSendToAnvil={handleSendToAnvil} />
          </section>
        )}

        {/* VIEW 5: LIVING IRON FORGE */}
        {(currentView === 'forge') && (
          <section id="forge-view" className="space-y-8 animate-fade-in" aria-labelledby="forge-title">
            <LivingIronForge />
          </section>
        )}

        {/* VIEW 6: FORTRESS GATE & DISCORD */}
        {(currentView === 'fortress') && (
          <section id="fortress-view" className="space-y-8 animate-fade-in" aria-labelledby="fortress-title">
            <FortressGate />
          </section>
        )}

      </main>

      {/* Structural Footer */}
      <Footer onViewChange={handleViewChange} />
    </div>
  );
};

export default App;
